import React from "react";
import styled from "styled-components";
import { Device } from "../../../components/device";
import ImageCrop from "../UI/Image/ImageCrop";
import Icon from "../Icon/Icon";
import "animate.css/animate.css";

//Usage:
/*
<PopUpCard
    image="https://via.placeholder.com/400x500"
    altText="Image alt text">
    Text
</PopUpCard>
*/

interface PopUpCardProps
{
    image?: string;
    video?: string;
    altText?: string;
    noImage?: boolean;
}

const PopUpCardWrap = styled.div`
    background-color: var(--color-white, white);
    color: var(--color-gray-60, black);
    border: none;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    color: currentColor;
    font-size: 1rem;
    padding: 0;
    z-index: 100;
    white-space: initial;
    position: relative;
    margin: 0 auto;
    max-height: 75vh;
    overflow-y: auto;
    line-height: 1.15;
    display: flex;
    flex-flow: column;
    width: 70vw;

    @media only screen and (min-width: 480px) {
        width: 450px;
        overflow: hidden;
        flex-flow: row;
        align-items: stretch;
    }
    ${Device.tabletLandscape} {
        width: 500px;
    }
    h1, h2, h3, h4, h5 {
        font-size: var(--text-xl, 1.25rem);
    }
    h1, h2, h3, h4, h5, h6,
    .text-wrap {
        margin-bottom: 1rem;
    }
    p {
        margin: 0;
    }
    p + p {
        margin-top: 1rem;
    }
    .popup-card__close {
        line-height: 1;
        position: absolute;
        right: 4px;
        bottom: 0px;
        background-color: white;
        color: black;
        &:hover {
            background-color: black;
            color: white;
        }
    }
`;

const PopUpCardMedia = styled.div`
    width: 100%;
    @media only screen and (min-width: 480px) {
        width: 300px;
    }
    img {
        width: 100%; 
        height: 100%; 
        object-fit: cover;
        object-position: center;
    }
`;

const PopUpCardText = styled.div<PopUpCardProps>`
    padding: 1.5rem 1.25rem;
    padding-bottom: .8rem;
    width: 100%;
    max-height: 200px;
    overflow-y: scroll;
    &::-webkit-scrollbar { width: var(--scrollbar-width, 3px);}
    &::-webkit-scrollbar-track { background: var(--scrollbar-track-color, lightgray); }
    &::-webkit-scrollbar-thumb { background: var(--scrollbar-thumb-color, gray); }
    &::-webkit-scrollbar-thumb:hover { background: var(--scrollbar-thumb-focus-color, black); }
    @media only screen and (min-width: 480px) {
        max-height: 300px;
    }
    @media only screen and (max-width: 479px) {
        max-height: none;
    }
    ${Device.tablet} {
        padding: 2rem 2rem 1rem 2rem;
    }
`;

export const CloseButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = (props) =>
{
    const { className, ...rest } = props;
    return (
        <button
            className={["popup-card__close", className].join(" ")}
            {...rest}>
            <Icon name="close" className="w-3 h-3 m-3 sm:m-4" />
        </button>
    );
};

const PopUpCard: React.FC<PopUpCardProps & React.HtmlHTMLAttributes<HTMLDivElement>> = (props) =>
{
    return (
        <PopUpCardWrap {...props}>
            {props.image ?
                <PopUpCardMedia>
                    <ImageCrop src={props.image} />
                </PopUpCardMedia>
                : null}

            <PopUpCardText>
                {props.children}
            </PopUpCardText>
        </PopUpCardWrap>
    );
};

export default PopUpCard;